import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { NodeShapeDTO } from '../../models/ShaclShapeSummary';
import { ShaclApiService } from '../../services/shacl-api.service';
import { ShapeDataService } from '../../services/shape-data.service';
import { generateTimestampedFilename, downloadJsonFile } from '../../utils/file-download.util';
import { VerifiableCredentialService } from '../../services/verifiable-credential.service';
import { UserService } from 'src/app/services/user-service/userservice';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';
import { finalize } from 'rxjs/operators';


@Component({
  selector: 'app-form',
  standalone: false,
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss']
})
export class FormComponent implements OnInit {
  shapeName?: string;
  shapeDescription?: string;
  shape?: NodeShapeDTO;
  loading = false;
  error?: string;

  fieldsPerPage = 10; // Default
  fieldsPerPageOptions = [1, 2, 5, 10];

  form!: FormGroup;

  @Output() close = new EventEmitter<void>();
  @Output() formValue = new EventEmitter<FormGroup>();
  @Output() formInitialized = new EventEmitter<FormGroup>();

  isOnboarding = false;
  prefillData: any = {};

  constructor(
    private route: ActivatedRoute,
    private api: ShaclApiService,
    private fb: FormBuilder,
    private shapeDataService: ShapeDataService,
    private vcService: VerifiableCredentialService,
    private userService: UserService,
    private router: Router,
    private organisationService: OrganisationService
  ) {
    this.form = this.fb.group({
    });
  }

  ngOnInit(): void {
    this.isOnboarding = this.router.url.includes('data-space-onboarding');
    this.loadPrefillData();
    this.loadShape();
  }

  private loadPrefillData(): void {
    const org = this.organisationService.activeOrganisation;
    if (org) {
      const name = org.organisationName || org.name;
      const street = org.address?.street;
      const postalCode = org.address?.postalCode;
      const locality = org.address?.city;
      const country = 'DE';

      // Use simplified, standard keys for internal mapping
      const addressData = {
        'streetaddress': street,
        'postalcode': postalCode,
        'locality': locality,
        'street': street,
        'zip': postalCode,
        'city': locality,
        'countrycode': country
      };

      this.prefillData = {
        'name': name,
        'legalname': name,
        'legaladdress': addressData,
        'headquartersaddress': addressData,
        'headquarteraddress': addressData,
        // Flattened for simple shapes
        'streetaddress': street,
        'postalcode': postalCode,
        'locality': locality
      };
    }
  }

  getPrefillValue(field: any): any {
    if (!field || (!field.path && !field.name)) return undefined;

    const path = (field.path || '').toLowerCase();
    const label = (field.name || '').toLowerCase();

    // Extract the significant part of the path (last segment after # or /)
    const segments = path.split(/[#/]/);
    const lastSegment = segments[segments.length - 1].replace(/[^a-z]/g, '');


    for (const key of Object.keys(this.prefillData)) {
      const lKey = key.toLowerCase().replace(/[^a-z]/g, '');
      if (lastSegment === lKey || path.includes(key) || label.includes(key)) {
        return this.prefillData[key];
      }
    }
    return undefined;
  }

  loadShape(): void {
    this.loading = true;
    this.error = undefined;
    this.shape = undefined;
    this.form = this.fb.group({
    });

    // Get shape from service
    const selectedShape = this.shapeDataService.getSelectedShape();

    if (selectedShape) {
      this.shape = selectedShape;
      this.shapeName = this.shapeDataService.getShapeName();
      this.shapeDescription = this.shapeDataService.getShapeDescription();
      this.initializeForm();
      this.loading = false;
    } else {
      this.error = 'No shape selected. Please go back and select a shape first.';
      this.loading = false;
    }
  }

  private initializeForm(): void {
    if (!this.shape) return;
    this.loading = true;

    // Reset form
    this.form = this.fb.group({
    });

    // Pre-calculate fields to ensure order
    const fields = this.shape.properties || [];

    // Add controls for top-level fields
    fields.forEach(field => {
      const key = this.makeControlKey(field.path);
      if (field.maxCount === null || field.maxCount > 1) {
        this.form.addControl(key, this.fb.array([]));
      } else if (field.clazz) {
        this.form.addControl(key, this.fb.group({}));
      } else {
        const validators = field.required ? [Validators.required] : [];
        const val = this.getPrefillValue(field) || '';
        this.form.addControl(key, this.fb.control(val, validators));
      }
    });

    this.loading = false;
    this.formInitialized.emit(this.form);
  }

  private makeControlKey(path: string): string {
    return path.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/_{2,}/g, '_');
  }
  get isLegalPerson(): boolean {
    const target = (this.shape?.targetClass || '').toLowerCase();
    return target.includes('legalperson') || target.includes('legalparticipant');
  }

  showOptionalFields = false;

  private readonly OPTIONAL_PATHS = [
    'gx:parentorganizationof',
    'gx:suborganisationof',
    'schema:description',
    'description'
  ];

  get fields() {
    if (!this.shape) return [];
    let props = [...(this.shape.properties || [])];

    if (this.isLegalPerson && !this.showOptionalFields) {
      props = props.filter(f => {
        const p = f.path.toLowerCase();
        return !this.OPTIONAL_PATHS.some(opt => p.endsWith(opt.toLowerCase()) || p === opt.toLowerCase());
      });
    }

    return props.sort((a: any, b: any) => {
      if (this.isLegalPerson) {
        const priority = (f: any) => {
          const p = f.path.toLowerCase();
          if (p.includes('name') || p.includes('legalname')) return 1;
          if (p.includes('registrationnumber')) return 2;
          if (p.includes('legaladdress')) return 3;
          return 100;
        };
        const pA = priority(a);
        const pB = priority(b);
        if (pA !== pB) return pA - pB;
      }

      const ao = a?.order ?? a?.ordinal ?? a?.index ?? Number.MAX_SAFE_INTEGER;
      const bo = b?.order ?? b?.ordinal ?? b?.index ?? Number.MAX_SAFE_INTEGER;
      if (ao === bo) {
        const an = a?.name || a?.path || '';
        const bn = b?.name || b?.path || '';
        return an.localeCompare(bn);
      }
      return ao - bo;
    });
  }

  get showOptionalButton(): boolean {
    if (!this.shape || !this.isLegalPerson || this.showOptionalFields) return false;
    return (this.shape.properties || []).some(f => this.OPTIONAL_PATHS.includes(f.path.toLowerCase()));
  }

  toggleOptionalFields(): void {
    this.showOptionalFields = true;
  }

  get primaryFields() {
    return this.fields.slice(0, this.fieldsPerPage);
  }

  get overflowFields() {
    return this.fields.slice(this.fieldsPerPage);
  }

  get hasOverflow() {
    return this.fields.length > this.fieldsPerPage;
  }



  onSubmit(): void {
    if (!this.shape) {
      alert('Error: No shape information available');
      return;
    }
    const formValue = this.form.value;
    this.loading = true;
    this.error = undefined;
    const userId = this.userService.currentUser?.userId;
    const orgId = this.route.snapshot.params['id'] || this.route.snapshot.parent?.params['id'] ||
      this.route.snapshot.params['organisationId'] || this.route.snapshot.parent?.params['organisationId'];

    this.vcService.saveToBackend({
      userId: userId ? Number(userId) : undefined,
      orgId: orgId ? Number(orgId) : undefined,
      name: this.shapeName || 'New SD',
      description: this.shapeDescription || '',
      targetClass: this.shape.targetClass,
      shapeId: this.shape.id,
      formData: formValue
    }).pipe(
      finalize(() => this.loading = false)
    ).subscribe({
      next: (response) => {
        // Response should be the SavedSdDTO
        const filename = generateTimestampedFilename(`vc_${this.shapeName || 'sd'}`, 'json');

        try {
          // If backend returns the content, we can still download it for the user
          if (response.content) {
            const credential = JSON.parse(response.content);
            // downloadJsonFile(credential, filename, true);
          }
          // alert(`Success! Self-Description saved to backend and assignment created.`);
          this.formValue.emit(this.form);
          this.close.emit();
        } catch (error) {
          alert('Saved to backend, but failed to prepare local download.');
        }
      },
      error: (err) => {
        this.error = 'Failed to save to backend: ' + (err.error?.message || err.message || 'Unknown error');
        alert(this.error);
      }
    });
  }

  private markAllAsTouched(group: FormGroup | FormArray): void {
    group.markAsTouched();
    Object.keys(group.controls).forEach(key => {
      const control = (group as any).get(key);
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAllAsTouched(control);
      } else {
        control.markAsTouched();
      }
    });
  }

  onReset(): void {
    this.form.reset();
  }

  goBack(): void {
    this.close.emit();
  }
}

